import { Directive, effect, inject, Injectable, input, InputSignal, Signal, signal, WritableSignal } from '@angular/core';

/**
 * Carries the `sizes` value of a layout container down to the images rendered inside it. Provided by
 * `ImageSizesDirective` and read by `gs-optimized-image` through the element injector, so a gallery
 * announces its slot width once on its grid instead of every item component threading the value
 * through its own inputs.
 */
@Injectable()
export class ImageSizesContext {
  // Properties & Signals
  /** The container's `sizes` value; empty until a directive sets it. */
  private readonly _sizes: WritableSignal<string> = signal<string>('');

  // Getters & Setters
  /** The container's `sizes` value, read-only for consumers. */
  readonly sizes: Signal<string> = this._sizes.asReadonly();

  // Methods
  /**
   * Sets the `sizes` value for every image inside the container.
   * @param sizes The `sizes` attribute value (e.g. "(min-width: 992px) 34vw, 100vw").
   */
  set(sizes: string): void {
    this._sizes.set(sizes);
  }
}

/**
 * Declares the `sizes` attribute for all `gs-optimized-image` instances inside this element. Put it on
 * the element whose width defines an image slot — a gallery grid, a swiper container — and the images
 * inside pick it up without every item component passing it on. An image that sets its own `sizes`
 * input keeps it; the container value only fills the gap.
 *
 * @example
 * <div class="grid" [gsImageSizes]="imageSizes">…</div>
 */
@Directive({
  selector: '[gsImageSizes]',
  providers: [ImageSizesContext]
})
export class ImageSizesDirective {
  // Services
  /** The context this directive provides to the images inside its element. */
  private readonly context: ImageSizesContext = inject(ImageSizesContext);

  // Inputs & Models
  /** The `sizes` value announced to the images inside this element. */
  gsImageSizes: InputSignal<string> = input<string>('');

  constructor() {
    // Keeps the provided context in sync with the bound value
    effect(() => this.context.set(this.gsImageSizes()));
  }
}
